/**
 * Set logflag when user loggedIn in Application
 * SetCookie when remMe is selected and Remove cookie when user is logOut
 */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import {
  LocalStorageService,
  SessionStorageService,
} from 'angular-web-storage';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  Subject,
  timeout,
  throwError,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { COOKIE_PREFERENCES_STORAGE_KEY } from '../privacy/cookie-preferences';
import { SKIP_AUTH_SESSION } from '../interceptors/auth-session-context';

interface AuthSessionResponse {
  success?: number;
  token?: string;
  accessTokenExpiresIn?: number;
  data?: any;
}

export interface DefaultProfileUpdate {
  profileId?: number | null;
  profileImage?: string | null;
  profileName?: string | null;
  publicName?: string | null;
  tagName?: string | null;
  imageVersion?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly SESSION_RESTORE_HINT = 'citizex_session_restore';
  private static readonly REMEMBER_ME_HINT_DAYS = 7;
  public loggedIn = false;
  private accessToken: string | null = null;
  private refreshInFlight$?: Observable<boolean>;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private refreshRetryDelayMs = 30_000;
  private readonly maxRefreshRetryDelayMs = 5 * 60_000;
  private readonly bootstrapRefreshTimeoutMs = 8_000;
  private readonly defaultProfileUpdatedSubject =
    new Subject<DefaultProfileUpdate>();
  readonly defaultProfileUpdated$ =
    this.defaultProfileUpdatedSubject.asObservable();

  constructor(
    private cookieService: CookieService,
    public session: SessionStorageService,
    public storage: LocalStorageService,
    private http: HttpClient
  ) {
    this.clearLegacyAuthStorage();
    this.sanitizeStoredUserData('userDTO');
    this.sanitizeStoredUserData('user');
    this.cookieService.delete('logUserDetails', '/');
  }

  /** Access tokens live in memory only and are lost on a browser reload. */
  SetLogFlag(): void {
    this.loggedIn = true;
  }

  getLogflag(): boolean {
    return this.loggedIn;
  }

  removeLogFlag(): void {
    this.loggedIn = false;
  }

  setSessionData(data: any) {
    this.session.set('userDTO', this.sanitizeUserData(data));
  }

  /**
   * Lets shell components immediately reflect a successfully saved default
   * profile without waiting for a route reload or another profile request.
   */
  notifyDefaultProfileUpdated(update: DefaultProfileUpdate): void {
    this.defaultProfileUpdatedSubject.next(update);
  }

  /**
   * Clears browser state immediately and revokes the refresh session when the
   * browser still has its httpOnly cookie. Existing callers use this method as
   * a synchronous cleanup hook, so the network revocation intentionally runs
   * in the background.
   */
  removeCookie(): void {
    this.clearClientSession(true, true);
    this.http
      .post(this.authUrl('logout'), {}, this.cookieRequestOptions())
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  getUser(): any {
    return this.sanitizeStoredUserData('userDTO');
  }

  getToken(): string | null {
    return this.accessToken;
  }

  setToken(token: string | null | undefined): void {
    this.accessToken = token?.trim() || null;
  }

  setUserDetails(data: any) {
    this.session.set('user', this.sanitizeUserData(data));
  }

  getUserDetails(): any {
    return this.sanitizeStoredUserData('user');
  }

  /** Call once during Angular bootstrap to restore an httpOnly refresh session. */
  initializeSession(): Observable<boolean> {
    this.clearLegacyAuthStorage();
    if (!this.cookieService.check(AuthService.SESSION_RESTORE_HINT)) {
      return of(false);
    }
    return this.refreshAccessToken().pipe(
      // APP_INITIALIZER awaits this call. A stalled Edge must not leave the
      // whole application blank while the browser waits on its socket timeout.
      timeout({ first: this.bootstrapRefreshTimeoutMs }),
      // A page load has no usable in-memory access token. Do not leave stale
      // user data behind if the refresh service is temporarily unavailable,
      // but preserve the httpOnly cookie so the next attempt can recover.
      catchError(() => {
        this.clearClientSession(false, false);
        this.scheduleRefreshRetry();
        return of(false);
      })
    );
  }

  startSession(
    response: AuthSessionResponse,
    rememberMe: boolean | undefined = undefined
  ): boolean {
    if (response?.success !== 0 || !response.token || !response.data) {
      this.clearClientSession(false, true);
      return false;
    }

    this.setToken(response.token);
    this.SetLogFlag();
    this.setSessionData(response.data);
    if (rememberMe !== undefined) {
      this.setSessionRestoreHint(rememberMe);
    }
    this.refreshRetryDelayMs = 30_000;
    this.scheduleRefresh(response.accessTokenExpiresIn);
    return true;
  }

  refreshAccessToken(): Observable<boolean> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.http
      .post<AuthSessionResponse>(
        this.authUrl('refresh'),
        {},
        this.cookieRequestOptions()
      )
      .pipe(
        map((response) => this.startSession(response)),
        catchError((error: unknown) => {
          const status = (error as { status?: number })?.status;
          if (status === 401 || status === 403) {
            this.clearClientSession(false, true);
            return of(false);
          }

          // A timeout or 5xx must not discard an otherwise valid browser
          // session. Callers can show their normal request error and a bounded
          // background retry will try again.
          return throwError(() => error);
        }),
        finalize(() => (this.refreshInFlight$ = undefined)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    return this.refreshInFlight$;
  }

  private authUrl(path: 'refresh' | 'logout'): string {
    const baseUrl = environment.serverUrl.endsWith('/')
      ? environment.serverUrl
      : `${environment.serverUrl}/`;
    return `${baseUrl}rest/auth/${path}`;
  }

  private cookieRequestOptions() {
    return {
      withCredentials: true,
      context: new HttpContext().set(SKIP_AUTH_SESSION, true),
    };
  }

  private scheduleRefresh(accessTokenExpiresIn: number | undefined): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }

    const ttlSeconds = Number(accessTokenExpiresIn);
    if (!Number.isFinite(ttlSeconds) || ttlSeconds <= 0) {
      return;
    }

    // The Edge accepts a 60-second minimum TTL. For short-lived development
    // tokens, refresh partway through their lifetime instead of scheduling at
    // zero seconds and silently relying on a later 401.
    const delaySeconds =
      ttlSeconds > 60
        ? ttlSeconds - 60
        : Math.max(5, Math.floor(ttlSeconds / 2));

    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = undefined;
      this.refreshAccessToken().subscribe({
        error: () => this.scheduleRefreshRetry(),
      });
    }, delaySeconds * 1000);
  }

  private scheduleRefreshRetry(): void {
    if (this.refreshTimer) {
      return;
    }

    const delay = this.refreshRetryDelayMs;
    this.refreshRetryDelayMs = Math.min(
      this.refreshRetryDelayMs * 2,
      this.maxRefreshRetryDelayMs
    );
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = undefined;
      this.refreshAccessToken().subscribe({
        error: () => this.scheduleRefreshRetry(),
      });
    }, delay);
  }

  private setSessionRestoreHint(rememberMe: boolean): void {
    this.cookieService.set(
      AuthService.SESSION_RESTORE_HINT,
      '1',
      rememberMe ? AuthService.REMEMBER_ME_HINT_DAYS : undefined,
      '/',
      undefined,
      typeof location !== 'undefined' && location.protocol === 'https:',
      'Lax'
    );
  }

  private clearClientSession(
    clearAllLocalStorage = false,
    clearSessionRestoreHint = false
  ): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    this.accessToken = null;
    this.loggedIn = false;
    this.refreshRetryDelayMs = 30_000;
    this.session.clear();
    if (clearAllLocalStorage) {
      this.clearLocalStoragePreservingCookiePreferences();
    }
    if (clearSessionRestoreHint) {
      this.cookieService.delete(AuthService.SESSION_RESTORE_HINT, '/');
    }
    this.clearLegacyAuthStorage();
  }

  private clearLocalStoragePreservingCookiePreferences(): void {
    let cookiePreferences: string | null = null;

    try {
      cookiePreferences = window.localStorage.getItem(
        COOKIE_PREFERENCES_STORAGE_KEY
      );
    } catch {
      // Preserve the existing logout behavior if browser storage is blocked.
    }

    this.storage.clear();

    if (cookiePreferences === null) {
      return;
    }

    try {
      window.localStorage.setItem(
        COOKIE_PREFERENCES_STORAGE_KEY,
        cookiePreferences
      );
    } catch {
      // Logout must still complete when browser storage cannot be restored.
    }
  }

  private clearLegacyAuthStorage(): void {
    this.storage.remove('token');
    this.storage.remove('loggedIn');
    this.storage.remove('userDTO');
    this.storage.remove('user');
    this.cookieService.delete('keekuUser', '/');
    this.cookieService.delete('logToken', '/');
  }

  private sanitizeStoredUserData(storageKey: string): any {
    const storedData = this.session.get(storageKey);
    const sanitizedData = this.sanitizeUserData(storedData);
    if (this.hasSensitiveUserData(storedData)) {
      this.session.set(storageKey, sanitizedData);
    }
    return sanitizedData;
  }

  private hasSensitiveUserData(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    return [
      'password',
      'drivingLicense',
      'socialSecurityNumber',
      'forgotPasswordToken',
      'recaptchaToken',
    ].some((field) => Object.prototype.hasOwnProperty.call(data, field));
  }

  private sanitizeUserData(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const {
      password,
      drivingLicense,
      socialSecurityNumber,
      forgotPasswordToken,
      recaptchaToken,
      ...safeUserData
    } = data;
    return safeUserData;
  }

  // set represenatative data
  setRepresentativeList(data: any) {
    this.storage.set('representativeList', data);
  }

  // get representative data
  getRepresentativeList(): any {
    return this.storage.get('representativeList');
  }

  // set eventDTO stored in session.
  setEventDetails(data: any) {
    this.storage.set('eventDTO', data);
  }

  // getting a eventDTO stored in session.
  getEventDetails(): any {
    return this.storage.get('eventDTO');
  }

  setProfileName(name: string) {
    this.storage.set('profileName', name);
  }

  getProfileName() {
    return this.storage.get('profileName');
  }

  createPoliticiansPage(data: boolean) {
    this.storage.set('politicianPage', data);
  }

  getPoliticiansPage() {
    return this.storage.get('politicianPage');
  }
}
